import React, { useMemo } from 'react'
import { Alert, ScrollView, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Button, Divider, List } from 'react-native-paper'
import { useRouter } from 'expo-router'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { observer } from 'mobx-react-lite'
import { BADGES, BADGE_IDS, tierName } from '@ppwr/convex/badges/catalog'

import { DS } from '../../design-system'
import { MainHeader } from '../../components/MainHeader/MainHeader'

/**
 * Dev tooling: grant tiers on / reset the signed-in user's own badges.
 * Route is admin-gated (admin/_layout.tsx); mutations also requireAdmin.
 */
export const AdminBadgesScreen = observer(() => {
  const router = useRouter()
  const myBadges = useQuery(api.badges.queries.getMyBadges, {})
  const grantBadge = useMutation(api.badges.mutations.grantBadgeForTesting)
  const resetBadges = useMutation(api.badges.mutations.resetMyBadgesForTesting)
  const resetBadgeDiamonds = useMutation(api.diamonds.mutations.resetMyBadgeDiamondsForTesting)

  const rowsByBadgeId = useMemo(() => new Map((myBadges ?? []).map((row) => [row.badgeId, row])), [myBadges])

  const onReset = () => {
    Alert.alert('Reset badges', 'Delete all of your badge progress?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Reset',
        style: 'destructive',
        // Reset every badge, then every diamond row badges paid, so re-earning pays again
        onPress: () =>
          resetBadges({})
            .then(() => resetBadgeDiamonds({}))
            .catch(() => {})
      }
    ])
  }

  return (
    <SafeAreaView style={styles.container}>
      <MainHeader
        leftPress={() => router.back()}
        title="Badges (Testing)"
        titleStyle={{ color: DS.palette['Global/Black'], marginLeft: DS.spacing[12] }}
      />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Button mode="contained" onPress={onReset} buttonColor={DS.palette['Brand/Maroon']}>
          Reset all my badges
        </Button>
        <Divider style={styles.divider} />
        {BADGE_IDS.map((id) => {
          const badge = BADGES[id]
          const row = rowsByBadgeId.get(id)
          const tier = row?.tierEarnedAt.length ?? 0
          const atTop = tier >= badge.tiers.length
          const status = atTop
            ? `${tierName(badge, tier)} (max)`
            : tier > 0
              ? `${tierName(badge, tier)} — ${row?.count ?? 0}/${badge.tiers[tier]}`
              : `Locked — ${row?.count ?? 0}/${badge.tiers[0]}`

          return (
            <View key={id}>
              <List.Item
                title={badge.name}
                description={status}
                right={() => (
                  <Button compact disabled={atTop} onPress={() => grantBadge({ badgeId: id }).catch(() => {})}>
                    Grant tier
                  </Button>
                )}
              />
              <Divider />
            </View>
          )
        })}
      </ScrollView>
    </SafeAreaView>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  scrollContent: {
    padding: DS.spacing[16],
    paddingBottom: DS.spacing[48]
  },
  divider: {
    marginVertical: DS.spacing[16]
  }
})